/**
 * Plain-text extraction for speaking pageContent (same block shapes as essay lesson pages).
 */

import type { PageElementData, BlockContent } from '@ai-content-hub/content-elements';

function extractTextFromTiptapJson(node: unknown): string {
    if (!node) return '';

    if (typeof node === 'string') return node;

    if (Array.isArray(node)) {
        return node.map(extractTextFromTiptapJson).join(' ').trim();
    }

    if (typeof node === 'object' && node !== null) {
        const obj = node as Record<string, unknown>;
        if (obj.type === 'text' && typeof obj.text === 'string') {
            return obj.text;
        }
        const content = obj.content;
        if (content) {
            return extractTextFromTiptapJson(content);
        }
    }

    return '';
}

function extractTextFromBlock(content: BlockContent | undefined): string {
    if (!content) return '';

    switch (content.type) {
        case 'text':
            return (content.text || '').trim();
        case 'rich-text': {
            const inner = content.content;
            if (typeof inner === 'string') return inner.trim();
            if (inner && typeof inner === 'object' && 'markdown' in inner) {
                const md = (inner as { markdown: string }).markdown;
                return typeof md === 'string' ? md.trim() : '';
            }
            return extractTextFromTiptapJson(inner).trim();
        }
        case 'audio':
            return (content.title || '').trim();
        case 'video':
            return (content.title || '').trim();
        case 'image':
            return (content.alt || '').trim();
        default:
            return '';
    }
}

/** Concatenate visible text from speaking `pageContent` sections (for AI question_content). */
export function extractTextFromSpeakingPage(page: PageElementData | null | undefined): string {
    if (!page || typeof page !== 'object') return '';

    const sections = page.sections;
    if (!Array.isArray(sections)) return '';

    const parts: string[] = [];

    for (const section of sections) {
        if (!section || typeof section !== 'object') continue;
        const blocks = section.blocks;
        if (!Array.isArray(blocks)) continue;

        for (const block of blocks) {
            if (!block || typeof block !== 'object') continue;
            const text = extractTextFromBlock(block.content as BlockContent);
            if (text) {
                parts.push(text);
            }
        }
    }

    return parts.join(' ').trim();
}

/** First image `src` in speaking page (for QT_SPEAK_PIC_FREE grading). */
export function extractFirstImageSrcFromSpeakingPage(
    page: PageElementData | null | undefined
): string | undefined {
    if (!page || typeof page !== 'object') return undefined;

    const sections = page.sections;
    if (!Array.isArray(sections)) return undefined;

    for (const section of sections) {
        if (!section || typeof section !== 'object') continue;
        const blocks = section.blocks;
        if (!Array.isArray(blocks)) continue;

        for (const block of blocks) {
            if (!block || typeof block !== 'object') continue;
            const c = block.content as BlockContent | undefined;
            if (c && typeof c === 'object' && c.type === 'image') {
                const src = (c as { src?: string }).src?.trim();
                if (src) return src;
            }
        }
    }

    return undefined;
}

export function dataUrlToBlob(dataUrl: string): Blob {
    const comma = dataUrl.indexOf(',');
    if (comma < 0) {
        throw new Error('Invalid data URL');
    }
    const meta = dataUrl.slice(0, comma);
    const base64 = dataUrl.slice(comma + 1);
    const mimeMatch = /data:([^;,]+)/.exec(meta);
    const mime = (mimeMatch?.[1] ?? 'application/octet-stream').trim();
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
}

function imageExtensionForMime(mime: string): string {
    const m = mime.toLowerCase();
    if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
    if (m.includes('png')) return 'png';
    if (m.includes('webp')) return 'webp';
    if (m.includes('gif')) return 'gif';
    return 'jpg';
}

/**
 * Picture fields for POST …/scoring/speaking when the question type needs an image (e.g. QT_SPEAK_PIC_FREE).
 * Prefers `picture_image_url` for http(s); uses `picture_file` for data URLs.
 */
export function picturePayloadForSpeakingGrade(
    page: PageElementData | null | undefined
): { picture_image_url?: string; picture_file?: File } {
    const src = extractFirstImageSrcFromSpeakingPage(page);
    if (!src) return {};

    if (/^https?:\/\//i.test(src)) {
        return { picture_image_url: src };
    }

    if (src.startsWith('data:image/')) {
        try {
            const blob = dataUrlToBlob(src);
            const ext = imageExtensionForMime(blob.type);
            return {
                picture_file: new File([blob], `picture.${ext}`, { type: blob.type }),
            };
        } catch {
            return {};
        }
    }

    return { picture_image_url: src };
}
